import { inject, Injectable } from '@angular/core';
import { Camera, CameraErrorCode, GalleryPhotos, MediaResult } from '@capacitor/camera';
import { CapacitorService, ErrorHandlerService, FlashMessageService } from '@libs/shared/common';
import { AppError } from '@libs/shared/models';

const REQUIRED_PLUGINS: string[] = ['Camera'];

// The camera plugin rejects with this code when the user closes the camera
const CANCEL_ERROR_CODE: string = CameraErrorCode.TakePhotoCancelled;

// The camera plugin rejects with this message when the user closes the media library
const CANCEL_ERROR_MESSAGE: string = 'User cancelled photos app';

const PHOTO_QUALITY: number = 90;

// Camera and media library return different objects. Only the web view path is needed
type PhotoResult = Pick<MediaResult, 'webPath'>;

@Injectable({ providedIn: 'root' })
export class PhotoPickerService {
  private readonly capacitorService: CapacitorService = inject(CapacitorService);
  private readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);
  private readonly flashMessageService: FlashMessageService = inject(FlashMessageService);

  // Old versions of the native app do not have the plugin
  public isAvailable(): boolean {
    return this.capacitorService.isCapacitorApp() && this.capacitorService.hasPlugins(REQUIRED_PLUGINS);
  }

  // The result is empty when the user closes the camera
  public takePhoto(): Promise<File[]> {
    return this.toFiles(async () => [await Camera.takePhoto({ quality: PHOTO_QUALITY })]);
  }

  // The result is empty when the user closes the media library
  public pickFromMediaLibrary(multiple: boolean, limit?: number): Promise<File[]> {
    // The native plugin needs a positive limit. Undefined means no limit.
    const normalizedLimit: number | undefined = limit !== undefined && limit > 0 ? limit : undefined;

    return this.toFiles(async () => {
      // pickImages converts every photo to JPEG, also the HEIC photos of an iPhone
      // chooseFromGallery returns the original file, which the upload does not accept
      const { photos }: GalleryPhotos = await Camera.pickImages({
        quality: PHOTO_QUALITY,
        limit: multiple ? normalizedLimit : 1,
      });

      return photos;
    });
  }

  private async toFiles(selectPhotos: () => Promise<PhotoResult[]>): Promise<File[]> {
    try {
      const photos: PhotoResult[] = await selectPhotos();

      return await Promise.all(photos.map((photo: PhotoResult) => this.toFile(photo)));
    } catch (error: unknown) {
      if (!this.isCancelError(error)) {
        this.errorHandler.handleError(error);
        this.flashMessageService.showMessage('errorPhotoPicker');
      }

      return [];
    }
  }

  // The file input works with File objects, so the photo is loaded from the web view path
  private async toFile(photo: PhotoResult): Promise<File> {
    if (!photo.webPath) {
      throw AppError.create('photo-picker', 500, 'Could not get web path of the photo');
    }

    const response: Response = await fetch(photo.webPath);

    if (!response.ok) {
      throw AppError.create('photo-picker', response.status, 'Could not load the photo');
    }

    const blob: Blob = await response.blob();
    const fileName: string = photo.webPath.slice(photo.webPath.lastIndexOf('/') + 1);

    return new File([blob], fileName, { type: blob.type });
  }

  private isCancelError(error: unknown): boolean {
    const { code, message }: { code?: string; message?: string } = error as { code?: string; message?: string };

    return code === CANCEL_ERROR_CODE || message === CANCEL_ERROR_MESSAGE;
  }
}
